<template>
  <div v-if="error">
    <div class="flex-row justify-center">
      <Message> Article could not be loaded: {{ error }} </Message>
    </div>
  </div>
  <div v-else-if="article" class="article">
    <div class="article-content">
      <!-- <h1>{{ article.title }}</h1> -->
      <div ref="articleImage" class="frame">
        <img :src="article.photo" alt="Article image." />
      </div>
      <div v-html="article.content" class="content" />
      <div class="date" v-if="new Date(article.pub_date) > new Date()">
        Will be published on
        {{ new Date(article.pub_date).toLocaleDateString() }}
      </div>
      <div class="date" v-else>
        Published on {{ new Date(article.pub_date).toLocaleDateString() }}
      </div>

      <div class="bottom-buttons">
        <button v-if="previousArticle" @click="previous">
          <i class="pi pi-chevron-left"></i>
          <span>Previous Post</span>
        </button>
        <div v-else></div>

        <button v-if="nextArticle" @click="next">
          <span>Next Post</span>
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
  <div v-else>Loading...</div>
</template>

<route lang="json">
{
  "name": "blog-article"
}
</route>

<script setup lang="ts">
import { usePulseStore } from "@/stores/pulse";
import { useHead } from "@unhead/vue";
import { onMounted } from "vue";
import { useRoute } from "vue-router";
// import type { Article, Category } from './Blog';
import type { Article } from "@paparazzi/components/blog/Blog";
import api from "@virgodev/bazaar/functions/api";
import Message from "primevue/message";
import { computed, nextTick, ref, watch } from "vue";

const pulseStore = usePulseStore();
const route = useRoute();

const props = defineProps<{
  slug: string;
}>();

const article = ref<Article>();
const error = ref<string>("");

const previousArticle = computed<Article | undefined>(() => {
  if (pulseStore.loaded) {
    const index = pulseStore.articles.findIndex(
      (a) => a.slug === article.value?.slug,
    );

    if (index > 0) {
      return pulseStore.articles[index - 1];
    }
  }

  return undefined;
});

const nextArticle = computed<Article | undefined>(() => {
  if (pulseStore.loaded) {
    const index = pulseStore.articles.findIndex(
      (a) => a.slug === article.value?.slug,
    );

    if (index < pulseStore.articles.length - 1) {
      return pulseStore.articles[index + 1];
    }
  }

  return undefined;
});

const articleImage = ref();

watch(props, async () => {
  article.value = await loadArticle(props.slug);
  await nextTick(); // Wait for DOM updates after v-html renders content
  updateLinks();
});

useHead({
  title: "Article",
});

onMounted(async () => {
  article.value = await loadArticle(props.slug);
  await nextTick(); // Wait for DOM updates after v-html renders content
  updateLinks();
  if (pulseStore.articles.length === 0 && !pulseStore.loaded) {
    pulseStore.loadArticles(null);
  }
});

async function loadArticle(slug: string): Promise<Article | undefined> {
  let retval: Article | undefined;
  const response = await api({ url: `blog/articles/${slug}/` });
  if (response.ok) {
    if (response.body?.detail) {
      error.value = response.body.detail;
    }
    retval = response?.body as Article;
  } else {
    error.value = "Failed to connecto to server";
  }
  return retval;
}

function updateLinks() {
  const links = document.querySelectorAll("a[href]");
  links.forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer"); // Optional for security
  });
}

async function next() {
  if (nextArticle.value) {
    article.value = nextArticle.value;
    articleImage.value.scrollIntoView(false);
  }
}

function previous() {
  if (previousArticle.value) {
    article.value = previousArticle.value;
    articleImage.value.scrollIntoView(false);
  }
}
</script>

<style scoped>
.article {
  margin: 0 10px;
}
.article .frame {
  margin: 0 auto 0 auto;
}

.content :deep(h1) {
  margin-top: 10px;
  margin-bottom: 48px;
  font-size: 18px;
  color: var(--Paparazzi-Pink, #ec008c);
  text-align: center;
  font-weight: 400;
  margin-top: 0;
}
.content :deep(h2) {
  font-size: 16px;
  font-weight: 300;
  text-align: left;
  color: var(--Black, #000);
  margin-top: 32px;
}

.content :deep(h3) {
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  color: var(--Black, #000);
}

.content :deep(p img) {
  margin: 34px 0;
  text-align: center;
  height: auto;
  max-width: 100%;
}

.content :deep(ul) {
  font-size: 14px;
  padding: 0 0 0 20px;
  li {
    margin-bottom: 15px;
    text-align: left;
    list-style-type: disc;
  }
}
.content :deep(a) {
  color: var(--Paparazzi-Pink);
}

.content :deep(ol) {
  padding-left: 20px;
  font-size: 14px;
  li {
    font-weight: 500;
    margin-bottom: 15px;
    p {
      margin-top: 0;
    }
  }
  li:last-child {
    margin-bottom: 0;
  }
}

.date {
  text-align: left;
  color: var(--Black, #000);
  font-size: 14px;
  margin: 48px 0;
}

.article .frame {
  max-width: 368px;
}

.article-content {
  max-width: 600px;
  margin: auto;
}
.article img {
  max-width: 100%;
  margin: auto;
  margin-bottom: 48px;
}

.article-content:deep ol li {
  text-align: left;
  list-style: list;
  font-weight: 400;
  margin-bottom: 15px;
}

.bottom-buttons {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 50px;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--50, #939598);
    background-color: transparent;
    border: none;
    cursor: pointer;

    i {
      font-size: 10px;
    }
  }
}

@media (max-width: 500px) {
  .content :deep(img) {
    max-width: 100%;
  }
  .article {
    margin: 0 32px;
  }
}
</style>
